import type { JSX } from "react";
import { Link, Outlet } from "react-router";
import type { AccessRight } from "../shared/auth/accessRights";
import { useI18n } from "../shared/i18n/I18nContext";
import { useMe } from "../shared/session/useMe";
import { useNavigationRoles } from "../shared/session/useNavigationRoles";
import { Loading } from "../shared/ui/Loading";
import { PageHeader } from "../shared/ui/PageHeader";
import styles from "../shared/ui/ui.module.css";
import { routePaths } from "./navigation";
import { routeAccess } from "./routeAccess";

/**
 * Neutral page for a restricted screen opened without the needed role, for example `/admin` by an
 * employee (UX-025): no administration interface whose every request would fail, but what this
 * means and the way to the start page.
 *
 * @returns The page.
 */
export function NoAccessPage(): JSX.Element {
  const { t } = useI18n();
  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("access.denied.title")} />
      <p>{t("access.denied.text")}</p>
      <div className={styles["row"]}>
        <Link className={styles["button"]} to={routePaths.clock}>
          {t("error.backHome")}
        </Link>
      </div>
    </div>
  );

  return page;
}

/** Properties of {@link RequireAccess}. */
export interface RequireAccessProps {
  /** Rights of which one opens the screens below. */
  readonly anyOf: readonly AccessRight[];
}

/**
 * Route guard of restricted screens (UX-025). It uses the same roles as the navigation: the roles
 * from `GET /me`, or after an app start without connection the last known roles of the same
 * person and tenant (F-05). While the profile still loads it shows the loading state; if the roles
 * stay unknown (offline without stored roles) it shows the screen, which handles the missing
 * connection itself. The guard only decides what the interface shows; the server stays
 * authoritative for every request.
 *
 * @param props - Component properties.
 * @returns The screen, the loading state or {@link NoAccessPage}.
 */
export function RequireAccess(props: RequireAccessProps): JSX.Element {
  const roles = useNavigationRoles();
  const me = useMe();
  const access = routeAccess(roles, props.anyOf);
  const element =
    access === "denied" ? (
      <NoAccessPage />
    ) : access === "unknown" && me.isLoading ? (
      <Loading />
    ) : (
      <Outlet />
    );

  return element;
}
